/**
 * About page — version, system changelog, community and contact.
 *
 * Update 3.2 (v3.2):
 *   - version text: "DeadMoonRadio: v3.2"
 *   - Update 3.2 system log entry (rebuilt mobile/iOS audio playback engine,
 *     lock-screen MediaSession integration, terminal stability improvements)
 *   - footer: "Music From the Heavens"
 *
 * The CRT layers are NOT rendered here anymore — Atmosphere is mounted once at
 * App level (v2 duplicated them on both routes, stacking two identical fixed
 * layers at the same z-index).
 */

import React from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Mail, Send } from 'lucide-react';
import { Link } from 'react-router-dom';
import { CONTACT_EMAIL, SITE, TELEGRAM_URL } from '@/lib/config';

const MOONS = [
  { duration: 1.5, delay: 0, from: 0.3, glow: '0 0 5px rgba(0,255,65,0.3)' },
  { duration: 1.2, delay: 0.3, from: 0.5, glow: '0 0 5px rgba(0,255,65,0.3)' },
  { duration: 1.8, delay: 0.6, from: 0.2, glow: '0 0 5px rgba(0,255,65,0.3)' },
];

function Field({ label, children }) {
  return (
    <div className="mb-8">
      <p className="dmr-label mb-2">{label}</p>
      {children}
    </div>
  );
}

export default function About() {
  return (
    <div className="relative flex min-h-[var(--viewport-h)] flex-col items-center justify-center bg-[#050505] px-4 py-12">
      <motion.div
        className="fixed left-6 top-6 z-[var(--z-overlay)]"
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 0.2 }}
      >
        <Link to="/" className="dmr-button dmr-button--ghost !px-3 !py-2" data-testid="back-link">
          <ArrowLeft className="h-4 w-4" />
          <span>Back to Radio</span>
        </Link>
      </motion.div>

      <div className="relative z-10 w-full max-w-2xl">
        <motion.div
          className="mb-12 text-center"
          initial={{ opacity: 0, y: -30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h1 className="dmr-heading mb-4 text-4xl tracking-tighter md:text-6xl">DeadMoon·Radio</h1>
          <div className="dmr-rule mx-auto h-px w-24" />
        </motion.div>

        <motion.div
          className="dmr-panel p-8 md:p-12"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          {/* Version — v3.2 */}
          <Field label="Version">
            <p className="text-lg text-[color:var(--phosphor)]" data-testid="version-text">
              {SITE.version} (Update 3.2)
            </p>
            <div
              className="mt-3 border-l border-[color:var(--phosphor-20)] pl-4 font-mono text-xs leading-relaxed text-[color:var(--bone)]/70"
              data-testid="changelog-text"
            >
              <p className="mb-1 text-[color:var(--phosphor-60)]">
                [sys.log // update 3.2 — transmission patch]
              </p>
              <ul className="space-y-1">
                <li>• rebuilt mobile &amp; iOS Safari direct-gesture audio playback engine</li>
                <li>• lock-screen &amp; control center MediaSession telemetry integration</li>
                <li>• terminal uplink &amp; stream lifecycle stability hardening</li>
              </ul>
            </div>
          </Field>

          {/* Community */}
          <Field label="Community">
            <a
              href={TELEGRAM_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-[color:var(--phosphor)] transition-opacity hover:opacity-80"
              data-testid="telegram-link"
            >
              <Send className="h-4 w-4" />
              <span>Telegram: t.me/DeadMoonArchives</span>
            </a>
          </Field>

          {/* Contact */}
          <Field label="Contact">
            <a
              href={`mailto:${CONTACT_EMAIL}`}
              className="flex items-center gap-2 text-[color:var(--phosphor)] transition-opacity hover:opacity-80"
              data-testid="contact-link"
            >
              <Mail className="h-4 w-4" />
              <span>Send hatemail, death threats, love letters, inquiries to:</span>
            </a>
            <p className="ml-6 mt-1 text-[color:var(--bone)]/70">{CONTACT_EMAIL}</p>
          </Field>

          {/* Footer — "Music From the Heavens" */}
          <div className="border-t border-[color:var(--edge)] pt-6">
            <p
              className="text-center text-sm italic text-[color:var(--phosphor-60)]"
              data-testid="footer-text"
            >
              {SITE.tagline}
            </p>
          </div>
        </motion.div>

        <motion.div
          className="mt-8 flex justify-center gap-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
          aria-hidden="true"
        >
          {MOONS.map((moon, index) => (
            <motion.span
              key={index}
              className="text-2xl text-[color:var(--phosphor)]"
              animate={{
                opacity: [moon.from, 1, moon.from],
                textShadow: [moon.glow, '0 0 22px rgba(0,255,65,0.85)', moon.glow],
              }}
              transition={{ duration: moon.duration, repeat: Infinity, delay: moon.delay }}
            >
              ☾
            </motion.span>
          ))}
        </motion.div>
      </div>
    </div>
  );
}
